import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { formatearFecha } from '@/lib/fecha'
import { Banner } from '@/ui/Banner'
import { Boton } from '@/ui/Boton'
import { CampoFormulario, EntradaTexto, Selector } from '@/ui/CampoFormulario'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { FilasEsqueleto } from '@/ui/Esqueleto'
import { InsigniaEstado } from '@/ui/InsigniaEstado'
import { ENCF, Monto } from '@/ui/Numero'
import { descargarGastos, useGastos } from '@/dominio/compras/useCompras'
import type { Gasto } from '@/dominio/compras/tipos'
import { EncabezadoPantalla, Tarjeta } from './-pantalla'
import { BotonDescarga, finDeMesIso, inicioDeMesIso } from './-contabilidad/comun'
import { ErrorCompras, Secciones, TD, TH } from './-compras/comun'
import { PanelGasto } from './-compras/PanelGasto'
import { DetalleGasto } from './-compras/DetalleGasto'
import { CategoriasGasto, Proveedores } from './-compras/Proveedores'

/**
 * Gastos (`docs/adr/0028` §2), del plan base: lo que se compra con o sin
 * comprobante, sus proveedores y sus categorías.
 *
 * **Un gasto con comprobante va al 606 sin duplicar lo recibido**: si el mismo
 * e-NCF llega a Recibidos, la base vincula los dos y declara el recibido.
 */

const SECCIONES = [
  { id: 'gastos', etiqueta: 'Gastos' },
  { id: 'proveedores', etiqueta: 'Proveedores' },
  { id: 'categorias', etiqueta: 'Categorías' },
] as const

type IdSeccion = (typeof SECCIONES)[number]['id']

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const Route = createFileRoute('/gastos')({
  validateSearch: (b: Record<string, unknown>): { seccion?: IdSeccion; gasto?: string } => ({
    seccion: SECCIONES.some((s) => s.id === b.seccion) && b.seccion !== 'gastos' ? (b.seccion as IdSeccion) : undefined,
    gasto: typeof b.gasto === 'string' && UUID.test(b.gasto) ? b.gasto : undefined,
  }),
  component: PantallaGastos,
})

function PantallaGastos() {
  const navegar = useNavigate({ from: Route.fullPath })
  const busqueda = Route.useSearch()
  const seccion = busqueda.seccion ?? 'gastos'

  return (
    <div className="flex flex-col gap-4">
      <EncabezadoPantalla
        titulo="Gastos"
        descripcion="Lo que compra con o sin comprobante. Con crédito fiscal va al 606 y adelanta su ITBIS; las retenciones van al 606 y al IR-17."
      />
      <Secciones
        etiqueta="Secciones de gastos"
        secciones={SECCIONES}
        actual={seccion}
        irA={(s) => void navegar({ search: { seccion: s === 'gastos' ? undefined : s }, replace: true })}
      />
      {seccion === 'gastos' ? (
        <ListaGastos
          abierto={busqueda.gasto ?? null}
          abrir={(id) => void navegar({ search: { gasto: id ?? undefined }, replace: true })}
        />
      ) : null}
      {seccion === 'proveedores' ? <Proveedores /> : null}
      {seccion === 'categorias' ? <CategoriasGasto /> : null}
    </div>
  )
}

function ListaGastos({ abierto, abrir }: { abierto: string | null; abrir: (id: string | null) => void }) {
  const [desde, setDesde] = useState(inicioDeMesIso())
  const [hasta, setHasta] = useState(finDeMesIso())
  const [texto, setTexto] = useState('')
  const [estado, setEstado] = useState('registrado')
  const gastos = useGastos({ desde, hasta, texto: texto.trim() || undefined, estado: estado || undefined })
  const [formulario, setFormulario] = useState<Gasto | 'nuevo' | null>(null)
  const lista = gastos.data?.gastos

  return (
    <>
      <Tarjeta
        titulo="Gastos del período"
        relleno={false}
        acciones={
          <div className="flex flex-wrap gap-2">
            <BotonDescarga etiqueta="Excel" descargar={() => descargarGastos(desde, hasta, 'xlsx')} />
            <Boton tamano="menor" variante="primario" icono={<Plus />} onClick={() => setFormulario('nuevo')}>
              Nuevo gasto
            </Boton>
          </div>
        }
      >
        <div className="grid gap-3 p-3 sm:grid-cols-4">
          <CampoFormulario etiqueta="Desde">
            {(a) => <EntradaTexto {...a} type="date" value={desde} max={hasta} onChange={(e) => setDesde(e.target.value)} />}
          </CampoFormulario>
          <CampoFormulario etiqueta="Hasta">
            {(a) => <EntradaTexto {...a} type="date" value={hasta} min={desde} onChange={(e) => setHasta(e.target.value)} />}
          </CampoFormulario>
          <CampoFormulario etiqueta="Buscar">
            {(a) => <EntradaTexto {...a} placeholder="Proveedor, RNC, NCF o descripción" value={texto} onChange={(e) => setTexto(e.target.value)} />}
          </CampoFormulario>
          <CampoFormulario etiqueta="Estado">
            {(a) => (
              <Selector {...a} value={estado} onChange={(e) => setEstado(e.target.value)}>
                <option value="registrado">Registrados</option>
                <option value="anulado">Anulados</option>
                <option value="">Todos</option>
              </Selector>
            )}
          </CampoFormulario>
        </div>

        {(gastos.data?.sinVincular ?? 0) > 0 ? (
          <div className="px-3 pb-3">
            <Banner tono="ambar" titulo={`${gastos.data?.sinVincular} gasto(s) declaran el comprobante de un recibido sin estar vinculados.`}>
              Ábralos y vincúlelos con su recibido: así el 606 lo cuenta una sola vez.
            </Banner>
          </div>
        ) : null}
        <ErrorCompras error={gastos.error} />

        {!lista ? (
          <div className="p-3">
            <FilasEsqueleto filas={6} columnas={6} />
          </div>
        ) : lista.length === 0 ? (
          <EstadoVacio
            variante={texto ? 'sin-resultados' : 'primera-vez'}
            mensaje={texto ? 'Ningún gasto coincide.' : 'Sin gastos en el período. Registre el primero, o lea la foto del comprobante.'}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-cuerpo">
              <thead className="bg-gris-50">
                <tr className="border-b border-gris-300">
                  <th className={TH}>Fecha</th>
                  <th className={TH}>Proveedor</th>
                  <th className={TH}>Comprobante</th>
                  <th className={TH}>Categoría</th>
                  <th className={`${TH} text-right`}>Total</th>
                  <th className={`${TH} text-right`}>Saldo</th>
                  <th className={TH}>606</th>
                </tr>
              </thead>
              <tbody>
                {lista.map((g) => (
                  <tr
                    key={g.id}
                    className="h-[var(--alto-fila)] cursor-pointer border-b border-gris-200 last:border-b-0 hover:bg-gris-50"
                    onClick={() => abrir(g.id)}
                  >
                    <td className={`${TD} numero whitespace-nowrap`}>{formatearFecha(g.fecha)}</td>
                    <td className={TD}>
                      <span className="block max-w-[260px] truncate">{g.nombreProveedor ?? '—'}</span>
                      <span className="block max-w-[260px] truncate text-etiqueta text-gris-600">{g.descripcion}</span>
                    </td>
                    <td className={TD}>{g.ncf ? <ENCF valor={g.ncf} abreviado copiable={false} /> : <span className="text-gris-600">Sin comprobante</span>}</td>
                    <td className={`${TD} text-gris-700`}>{g.categoriaNombre}</td>
                    <td className={`${TD} text-right`}>
                      <Monto valor={g.total} />
                    </td>
                    <td className={`${TD} text-right`}>{g.formaPago === 4 ? <Monto valor={g.saldo} /> : <span className="text-gris-600">contado</span>}</td>
                    <td className={TD}>
                      {g.estado === 'anulado' ? (
                        <InsigniaEstado tono="neutral">anulado</InsigniaEstado>
                      ) : g.recibidoId ? (
                        <InsigniaEstado tono="azul">por el recibido</InsigniaEstado>
                      ) : g.vaAl606 ? (
                        <InsigniaEstado tono="verde">va al 606</InsigniaEstado>
                      ) : (
                        <InsigniaEstado tono="neutral">no va</InsigniaEstado>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Tarjeta>

      {abierto ? (
        <DetalleGasto
          id={abierto}
          onCerrar={() => abrir(null)}
          onEditar={(g) => {
            abrir(null)
            setFormulario(g)
          }}
        />
      ) : null}
      {formulario ? (
        <PanelGasto
          key={formulario === 'nuevo' ? 'nuevo' : formulario.id}
          gasto={formulario === 'nuevo' ? null : formulario}
          onCerrar={() => setFormulario(null)}
          onGuardado={() => undefined}
        />
      ) : null}
    </>
  )
}

